// TriggerWatch Packages page — MSP portal
// Customers upload deployment packages (PowerShell, batch, etc.) and manage their package library

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Navbar from "../components/Navbar";
import useSuspended from "../hooks/useSuspended";

const API_URL = config.API_URL;

const PAYLOAD_TYPES = ["powershell", "batch", "cmd", "msi", "exe", "msix"];

export default function TriggerWatchPackages() {
  const isSuspended = useSuspended();
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const ORG_ID = storedUser.organization_id;

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState("");
  const [version, setVersion] = useState("");
  const [description, setDescription] = useState("");
  const [payloadType, setPayloadType] = useState("powershell");
  const [installArgs, setInstallArgs] = useState("");
  const [file, setFile] = useState(null);
  const [useInlineCommand, setUseInlineCommand] = useState(false);
  const [inlineCommand, setInlineCommand] = useState("");

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [editingPackageId, setEditingPackageId] = useState(null); // null = create mode, otherwise editing this package

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    fetchPackages();
  }, []);

  async function fetchPackages() {
    try {
      const res = await axios.get(`${API_URL}/organizations/${ORG_ID}/tw-packages`, authHeaders);
      setPackages(res.data);
    } catch (err) {
      console.error("Failed to load packages", err);
    } finally {
      setLoading(false);
    }
  }

function startEdit(pkg) {
    setEditingPackageId(pkg.id);
    setName(pkg.name);
    setVersion(pkg.version);
    setDescription(pkg.description || "");
    setPayloadType(pkg.payload_type);
    setInstallArgs(pkg.install_args || "");
    setUseInlineCommand(false); // always start in file mode when editing — existing payload is kept unless they choose to replace it
    setInlineCommand("");
    setFile(null);
    setSuccessMsg("");
    setErrorMsg("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function cancelEdit() {
    setEditingPackageId(null);
    setName("");
    setVersion("");
    setDescription("");
    setPayloadType("powershell");
    setInstallArgs("");
    setUseInlineCommand(false);
    setInlineCommand("");
    setFile(null);
    setErrorMsg("");
    setSuccessMsg("");
  }

  async function handleSubmit() {
    const isEditing = !!editingPackageId;

    if (!name.trim() || !version.trim()) {
      setErrorMsg("Please provide a name and version.");
      return;
    }
    if (useInlineCommand && !inlineCommand.trim()) {
      setErrorMsg("Please enter a command.");
      return;
    }
    // When creating, a payload is required. When editing, it's optional — leaving both
    // blank just keeps whatever payload the package already has.
    if (!isEditing && !useInlineCommand && !file) {
      setErrorMsg("Please choose a file to upload.");
      return;
    }

    setSubmitting(true);
    setErrorMsg("");
    setSuccessMsg("");

    const formData = new FormData();
    formData.append("name", name);
    formData.append("version", version);
    formData.append("description", description);
    formData.append("payload_type", payloadType);
    formData.append("install_args", installArgs);
    if (useInlineCommand) {
      formData.append("inline_command", inlineCommand);
    } else if (file) {
      formData.append("file", file);
    }

    try {
      if (isEditing) {
        await axios.put(`${API_URL}/organizations/${ORG_ID}/tw-packages/${editingPackageId}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        setSuccessMsg("✅ Package updated successfully!");
        cancelEdit();
      } else {
        await axios.post(`${API_URL}/organizations/${ORG_ID}/tw-packages`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        setSuccessMsg("✅ Package created successfully!");
        setName("");
        setVersion("");
        setDescription("");
        setPayloadType("powershell");
        setInstallArgs("");
        setFile(null);
        setInlineCommand("");
        const fileInput = document.getElementById("tw-file-input");
        if (fileInput) fileInput.value = "";
      }
      fetchPackages();
    } catch (err) {
      setErrorMsg(isEditing ? "❌ Failed to update package. Please try again." : "❌ Failed to create package. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(pkgId, pkgName) {
    if (!window.confirm(`Delete package "${pkgName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/organizations/${ORG_ID}/tw-packages/${pkgId}`, authHeaders);
      fetchPackages();
    } catch (err) {
      alert("Failed to delete package. Please try again.");
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navbar orgName={storedUser.organization_name} />

      <div className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-2xl font-bold text-white mb-1">TriggerWatch Packages</h1>
        <p className="text-gray-400 text-sm mb-8">
          Upload deployment packages that can be pushed out to your devices.
        </p>

        {/* Create form */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-10">
          <h2 className="text-lg font-semibold text-white mb-4">{editingPackageId ? "Edit Package" : "Create a Package"}</h2>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Chrome Silent Install"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Version</label>
              <input
                type="text"
                value={version}
                onChange={e => setVersion(e.target.value)}
                placeholder="e.g. 1.0.0"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What does this package do?"
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-1">Payload Type</label>
            <select
              value={payloadType}
              onChange={e => setPayloadType(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
            >
              {PAYLOAD_TYPES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <div className="flex items-center gap-4 mb-3">
              <button
                type="button"
                onClick={() => setUseInlineCommand(false)}
                className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${!useInlineCommand ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-400"}`}
              >
                Upload File
              </button>
              <button
                type="button"
                onClick={() => setUseInlineCommand(true)}
                disabled={!["powershell", "batch", "cmd"].includes(payloadType)}
                className={`text-sm px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${useInlineCommand ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-400"}`}
              >
                Enter Command
              </button>
              {!["powershell", "batch", "cmd"].includes(payloadType) && (
                <span className="text-gray-600 text-xs">Inline commands only work with powershell, batch, or cmd</span>
              )}
            </div>

            {editingPackageId && (
              <p className="text-gray-600 text-xs mb-2">Leave blank to keep the existing payload — only fill this in if you want to replace it.</p>
            )}

            {useInlineCommand ? (
              <textarea
                value={inlineCommand}
                onChange={e => setInlineCommand(e.target.value)}
                placeholder={
                  payloadType === "powershell"
                    ? 'e.g. Get-Package "App Name" | Uninstall-Package -Force'
                    : payloadType === "cmd"
                    ? 'e.g. wmic product where name="App Name" call uninstall /nointeractive'
                    : 'e.g. taskkill /F /IM appname.exe\nwmic product where name="App Name" call uninstall /nointeractive'
                }
                rows={4}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono resize-none"
              />
            ) : (
              <input
                id="tw-file-input"
                type="file"
                onChange={e => setFile(e.target.files[0])}
                className="w-full text-sm text-gray-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white file:text-sm hover:file:bg-blue-500 file:cursor-pointer cursor-pointer"
              />
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-1">Install Arguments (optional)</label>
            <input
              type="text"
              value={installArgs}
              onChange={e => setInstallArgs(e.target.value)}
              placeholder="e.g. /S /D=C:\Program Files\App  or  /qn REBOOT=ReallySuppress"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono"
            />
            <p className="text-gray-600 text-xs mt-1">Leave blank to use the default silent-install flags for the selected payload type.</p>
          </div>

          {successMsg && <p className="text-green-400 text-sm mb-3">{successMsg}</p>}
          {errorMsg && <p className="text-red-400 text-sm mb-3">{errorMsg}</p>}

          <div className="flex items-center gap-3">
            <button
              onClick={handleSubmit}
              disabled={submitting || isSuspended}
              className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors"
            >
              {submitting ? (editingPackageId ? "Saving..." : "Uploading...") : (editingPackageId ? "Save Changes" : "Create Package")}
            </button>
            {editingPackageId && (
              <button
                onClick={cancelEdit}
                className="bg-transparent border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Package list */}
        <h2 className="text-lg font-semibold text-white mb-4">Your Packages</h2>

        {loading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : packages.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500 text-sm">
            No packages yet. Create one above.
          </div>
        ) : (
          <div className="space-y-4">
            {packages.map(pkg => (
              <div key={pkg.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-medium text-sm">{pkg.name}</h3>
                      <span className="text-gray-500 text-xs">v{pkg.version}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400">
                        {pkg.payload_type}
                      </span>
                    </div>
                    {pkg.description && (
                      <p className="text-gray-400 text-sm mb-2">{pkg.description}</p>
                    )}
                    {pkg.install_args && (
                      <p className="text-gray-500 text-xs font-mono mb-2">Args: {pkg.install_args}</p>
                    )}
                    <p className="text-gray-600 text-xs">
                      {pkg.file_size} · Created by {pkg.created_by} ·{" "}
                      {new Date(pkg.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 whitespace-nowrap">
                    <button
                      onClick={() => navigate(`/triggerwatch/deployments?package_id=${pkg.id}`)}
                      disabled={isSuspended}
                      className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
                        isSuspended ? "bg-gray-800 text-gray-500 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-500 text-white"
                      }`}
                    >
                      Deploy
                    </button>
                    <button
                      onClick={() => startEdit(pkg)}
                      disabled={isSuspended}
                      className={`text-sm transition-colors ${isSuspended ? "text-gray-600 cursor-not-allowed" : "text-gray-400 hover:text-white"}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(pkg.id, pkg.name)}
                      disabled={isSuspended}
                      className={`text-sm transition-colors ${isSuspended ? "text-gray-600 cursor-not-allowed" : "text-red-400 hover:text-red-300"}`}
                    >
                      Delete
                    </button>
                  
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}